var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { observable, observe, computed, action, toJS } from "mobx";
import { computedFn } from "mobx-utils";
import { DeviceDetect, ui } from "../../../dependencies";
import { uic } from "../../../contracts";
import { MainGroupFunctions, SubGroupFunctions } from "../../../typings";
import { EventTypes, MainGroupConfiguration, SubGroupConfiguration, CardType, FilterPredicate, GroupingFilterTypes, LiveStreamingConfiguration } from "../../../data";
import { StreamingFilterBlockConstant } from "../../../constants";
import * as helpers from "../../../helpers";
import { sortEventsByTime } from "../../../composables/sorters";
import { componentManagerConfig } from "../config";
export class EventListPresenterState {
    constructor(provider, options) {
        this.EVENTS_CHUNK_SIZE = 20;
        this.getMarketFilterConfig = computedFn((sportId, mainHeading) => {
            return this.shouldRenderMarketFilter(sportId, mainHeading)
                && !this.shouldRenderGameLines(mainHeading, true)
                ? Object.assign(Object.assign({}, this.getFilterConfigDefault()), { options: helpers.getMarketOptions(this.provider.getCardConfigBySportId(sportId, this.isMiniEventCard).markets, this.options.dict, sportId), onChangeCallback: this.onMarketTypeChange.bind(this) }) : undefined;
        });
        this.getFilterConfig = computedFn((mainHeading) => {
            if (this.options.cms.IsLiveSchedule) {
                return this.dateFilterConfig;
            }
            const cardConfigurationIds = Object.keys(this.options.dataProvider.cardConfiguration);
            const sportId = cardConfigurationIds.includes(mainHeading.sportId)
                ? mainHeading.sportId
                : cardConfigurationIds[0];
            return this.getMarketFilterConfig(sportId, mainHeading);
        });
        this.displayEvents = computedFn((id) => {
            const filteredEvents = this.shouldHideSubheadings
                ? this[MainGroupFunctions[this.mainGroupBy].events](id)
                : this[SubGroupFunctions[this.subGroupBy].events](id);
            const displayEvents = this.filterEventsByType(filteredEvents);
            return displayEvents;
        });
        this.displayMarkets = computedFn((eventId) => {
            return this.provider.markets.filter(x => x && x.eventId === eventId);
        });
        this.displayLeagues = computedFn((sportId) => {
            const currentSportId = sportId;
            return this.provider.leagues.filter((l) => l.sportId === currentSportId);
        });
        // Events
        this.displayEventsBySportId = computedFn((sportId) => {
            let events = this.provider.events.filter(event => event.sportId === sportId);
            if (this.provider.shouldSortEventsByTime) {
                events.sort(sortEventsByTime);
            }
            return events;
        });
        this.displayEventsByLeague = computedFn((leagueId) => {
            let events = this.provider.events.filter(ev => ev.leagueId === leagueId);
            if (this.isInFavoritesState) {
                return this.filterFavoriteEvents(events, true);
            }
            if (this.showFavoritesSection) {
                return this.filterFavoriteEvents(events, false);
            }
            return events.sort(sortEventsByTime);
        });
        this.displayEventsByTime = computedFn((hour) => {
            return this.provider.events.filter(ev => {
                const evDate = helpers.getISODate(ev.startEventDate, this.provider.timeZone);
                return evDate && evDate.getHours().toString() === hour;
            });
        });
        this.displayEventsByDay = computedFn((id) => {
            return this.provider.events.filter((event) => {
                const evDate = helpers.getISODate(event.startEventDate, this.provider.timeZone);
                const dateId = evDate && this.getDayId(evDate).toString();
                return dateId === id;
            }).sort(sortEventsByTime);
        });
        this.displayEventsById = computedFn((id) => {
            let event = this.provider.events.find((ev) => ev.id === id);
            return event ? [event] : [];
        });
        this.displayFavoriteEventsByLeague = computedFn((id) => {
            return this.provider.events.filter((ev) => ev.isInFavorites && ev.leagueId === id);
        });
        this.filterEventsByType = (events) => {
            const eventsWithMarkets = events.filter(this.filterOutrightWithMarkets);
            if (this.showLiveFixturesAndOutrights) {
                return eventsWithMarkets;
            }
            const fixtureEvents = eventsWithMarkets.filter(this.isFixture);
            return fixtureEvents.length ? fixtureEvents : eventsWithMarkets;
        };
        this.filterOutrightWithMarkets = (ev) => {
            return ev.type === EventTypes.Outright
                ? this.displayMarkets(ev.id).length > 0
                : true;
        };
        this.provider = provider;
        this.options = options;
        this.deviceDetect = DeviceDetect.getInstance();
        this.sports = provider.sports;
        this.dateRange = provider.dateRange;
        this.mainGroupBy = options.cms.MainGroupBy;
        this.subGroupBy = options.cms.SubGroupBy;
        this.isFavoritesEnabled = this.provider.isFavoritesEnabled;
        this.isInFavoritesState = this.provider.isInFavoritesState;
        this.showLiveFixturesAndOutrights = options.cms.ShowLiveFixturesAndOutrights;
        this.showOddBtns = options.cms.ShowOddsButtons;
        this.loader = this.provider.loader;
        this.componentManager = new helpers.ComponentManager(this.showLeagueInEventCard, this.options.cms.NumberOfSelectionsToShow, this.shouldShowOutrightSubheading, this.deviceDetect, this.options.dataProvider.componentManagerConfig || componentManagerConfig);
        this.filterManager = provider.filterManager;
        this.disposers = [];
        this.isYourBetModeEnabled = false;
        this.isMarketsDropdownFilterEnabled = options.cms.EnableMarketsDropdownFilter;
        this.viewMore = this.viewMore.bind(this);
    }
    get isMiniEventCard() {
        return this.provider.isMiniEventCard;
    }
    get showViewMore() {
        return !this.provider.allEventsLoaded;
    }
    get tooltipMessage() {
        return this.showOddBtns
            ? this.options.dict.HideAllOdds
            : this.options.dict.ShowAllOdds;
    }
    toggleOddsBtnOnClick() {
        this.showOddBtns = !this.showOddBtns;
        return this.showOddBtns;
    }
    activate() {
        this.attachObservable();
        this.provider.activate();
    }
    deactivate() {
        this.provider.deactivate();
        this.dettachObservable();
    }
    attachObservable() {
        this.disposers = [
            observe(this.provider, this.updateProviderProps)
        ];
    }
    updateProviderProps(change) {
        let provider = change.object;
        this.sports = provider.sports;
        this.dateRange = provider.dateRange;
        this.isFavoritesEnabled = provider.isFavoritesEnabled;
        this.isInFavoritesState = provider.isInFavoritesState;
        this.loader = provider.loader;
        this.isYourBetModeEnabled = provider.isYourBetModeEnabled;
        if (this.isYourBetModeEnabled) {
            this.showOddBtns = false;
        }
    }
    dettachObservable() {
        this.disposers.map(x => typeof x === "function" && x());
        this.disposers = [];
    }
    get isLoading() {
        return this.isGroupedByTime
            ? this.loader.isFirstLoad
            : this.loader.isLoading;
    }
    get hasLoadEvents() {
        return !this.isLoading && this.provider.events.length > 0;
    }
    get preliveOutrightShouldExpand() {
        return this.provider.markets.length === 1;
    }
    get showLeagueInEventCard() {
        return this.options.cms.ShowLeagueInEventCard && this.mainGroupBy !== MainGroupConfiguration.League
            && this.subGroupBy !== SubGroupConfiguration.League;
    }
    get getSelectedMarketTypeId() {
        return this.provider.activeMarketIds.join(",") || "";
    }
    get getSportGroupingFilterConfig() {
        return {
            shouldShow: !this.isMiniEventCard && this.options.cms.GroupingFilterType === GroupingFilterTypes.SportGroupingFilter,
            tooltipMessage: this.options.dict.SortBy,
            defaultValue: this.subGroupBy,
            options: [
                { label: `${this.options.dict.SortBy} ${this.options.dict.League}`, value: SubGroupConfiguration.League },
                { label: `${this.options.dict.SortBy} ${this.options.dict.Time}`, value: SubGroupConfiguration.Time }
            ],
            isDesktop: this.deviceDetect.isDesktop,
            handler: this.changeSubGrouping
        };
    }
    get getLiveStreamingGroupingFilterConfig() {
        return {
            shouldShow: this.provider.liveScheduleHasLiveStreaming && this.options.cms.GroupingFilterType === GroupingFilterTypes.LiveStreamingFilter,
            tooltipMessage: this.options.dict.GroupBy,
            defaultValue: this.options.cms.GroupingFilterDefaultValue,
            options: [
                { label: this.options.dict.LiveStreaming, value: LiveStreamingConfiguration.LiveStreaming },
                { label: this.options.dict.AllGames, value: LiveStreamingConfiguration.AllGames }
            ],
            isDesktop: this.deviceDetect.isDesktop,
            handler: this.changeLiveStreamingFilter
        };
    }
    get dateFilterConfig() {
        const today = this.provider.getBeggingOfDay(this.provider.timeZone, new Date());
        const next7days = this.provider.next7Days(today);
        const selectedDate = next7days[this.provider.selectedDay];
        return Object.assign(Object.assign({}, this.getFilterConfigDefault()), { options: helpers.getDateOptions(next7days, selectedDate, this.options.dict, next7days), onChangeCallback: this.onDateChange.bind(this) });
    }
    getFilterConfigDefault() {
        return {
            id: "evlist-heading-dropdown",
            dataUat: "evlist-heading-dropdown",
            isDesktop: this.deviceDetect.isDesktop
        };
    }
    shouldRenderMarketFilter(sportId, mainHeading) {
        return this.options.dataProvider.cardConfiguration[sportId].cardType === CardType.Regular
            && !mainHeading.inLiveStreaming
            && !this.isMiniEventCard
            && this.isMarketsDropdownFilterEnabled
            && !this.provider.isPersonalizedHighlights;
    }
    shouldRenderGameLines(mainHeading, isMain) {
        return !mainHeading.inLiveStreaming
            && mainHeading.marketsFilterEnabled
            && this.provider.getCardConfigBySportId(mainHeading.sportId, this.isMiniEventCard).cardType === CardType.American
            && isMain;
    }
    addToSlipTranspiler(isSelectionInBetslip, items) {
        return isSelectionInBetslip
            ? this.provider.sendBsUtilItemsRemoveMessage(items)
            : this.provider.sendBsUtilItemsAddMessage(items);
    }
    openGameTranspiler(event, ev) {
        if (ev) {
            ev.stopPropagation();
            ev.preventDefault();
        }
        let openGameData = this.provider.getOpenGameData(event, ev);
        openGameData &&
            this.options.navHandler.OpenGame(openGameData.event, openGameData.id, openGameData.isLive, openGameData.evTypeForGameBlock, openGameData.shouldHideSidePanels, openGameData.branchID);
    }
    manageFavorites(event, ev) {
        if (ev) {
            ev.stopPropagation();
            ev.preventDefault();
        }
        this.provider.manageFavorites(event);
    }
    openStatTranspiler(statisticsEventId, ev) {
        if (ev) {
            ev.stopPropagation();
            ev.preventDefault();
        }
        this.provider.openStats(statisticsEventId, ev);
    }
    openLiveStream(eventId, ev) {
        if (this.options.dataProvider.isLiveStreamHandlerEnabled && !this.isMiniEventCard) {
            this.provider.openLiveStream(eventId);
            if (ev) {
                ev.stopPropagation();
                ev.preventDefault();
            }
        }
    }
    changeSubGrouping(newGrouping) {
        this.subGroupBy = newGrouping;
        this.componentManager.showLeagueInEventCard = this.showLeagueInEventCard;
    }
    changeLiveStreamingFilter(newOption) {
        newOption === LiveStreamingConfiguration.LiveStreaming
            ? this.provider.liveStreamChange()
            : this.provider.clearEventTags();
    }
    openLeagueTranspiler(sportId, leagueId, ev, element) {
        if (ev) {
            ev.stopPropagation();
            ev.preventDefault();
        }
        this.options.navHandler.OpenLeague(ev, leagueId, sportId, element);
    }
    openViewAllLiveLink(ev) {
        if (ev) {
            ev.stopPropagation();
            ev.preventDefault();
        }
        let sportId = this.sports.length ? this.sports[0] : 0;
        if (this.filterManager.isCurrentMainFilter(FilterPredicate.LiveStreaming)) {
            sportId = StreamingFilterBlockConstant;
        }
        if (this.deviceDetect.isDesktop) {
            this.options.navHandler.OpenGame(ev, 0, true, 0, 0, sportId);
        }
        else {
            this.options.navHandler.OpenMobileLiveBetting(ev, 0, sportId);
        }
    }
    getViewAllLiveLink() {
        return this.options.navHandler.Navigation.getLiveBettingLink();
    }
    viewMore() {
        this.provider.viewMore();
    }
    // General display functions
    get displayMainHeadings() {
        const mainHeadings = this[MainGroupFunctions[this.mainGroupBy].headings](this.sports[0]);
        if (this.options.cms.IsLiveSchedule) {
            return mainHeadings;
        }
        return mainHeadings.filter((main) => this.displayEventByMainGrouping(this.getMainHeadingParameter(main.id)).length > 0);
    }
    displaySubheadings(sportId) {
        const subheadings = this[SubGroupFunctions[this.subGroupBy].headings](this.getSubHeadingParameter(sportId));
        return subheadings.map((s) => {
            const sub = toJS(s);
            sub.displayEvents = this.displayEvents(sub.id);
            return sub;
        }).filter((sub) => sub.displayEvents.length || this.isFavouriteLeague(sub));
    }
    getMainHeadingParameter(id) {
        return this.mainGroupBy === MainGroupConfiguration.Market
            ? this.getSelectedMarketTypeId
            : id;
    }
    getSubHeadingParameter(id) {
        return this.subGroupBy === SubGroupConfiguration.Market
            ? this.getSelectedMarketTypeId
            : id;
    }
    // TODO: pass both liveId and preLiveId as value
    onMarketTypeChange(_, selectedMarketTypeIds) {
        this.provider.selectedMarketIdChange(this.sports, selectedMarketTypeIds.split(","));
    }
    onDateChange(_, selectedDate) {
        const splitted = selectedDate.split("#");
        this.provider.selectedDateChange(splitted[0], Number.parseInt(splitted[1]));
    }
    // Display functions depending on current grouping
    // Main headings and Subheadings
    displayAllHeading(id) {
        return [{
                id: this.sports[0],
                sportId: this.sports[0],
                name: helpers.getDictPhrase(this.options.dict, "DisplayAllEvents"),
                inLiveStreaming: this.filterManager.isCurrentMainFilter(FilterPredicate.LiveStreaming),
                marketsFilterEnabled: false
            }];
    }
    displaySports(id) {
        let sportHeadings = [];
        this.sports.map((sportId) => {
            const event = this.provider.events.find((ev) => ev.sportId === sportId);
            event && sportHeadings.push({
                id: event.sportId,
                sportId: event.sportId,
                name: event.sportName,
                inLiveStreaming: this.filterManager.isCurrentMainFilter(FilterPredicate.LiveStreaming),
                marketsFilterEnabled: this.isMarketsDropdownFilterEnabled && !this.isMiniEventCard && !this.provider.isPersonalizedHighlights
            });
        });
        return sportHeadings;
    }
    displayHours(sportId) {
        if (this.dateRange && this.dateRange.from) {
            const from = this.dateRange.from;
            const now = helpers.addHours(new Date(), this.provider.timeZone);
            const end = helpers.addDays(this.dateRange.from, 1);
            const isToday = from <= now && now <= end;
            if (isToday) {
                let hours = helpers.getDailyHours(this.provider.formatTime, now.getUTCHours(), this.provider.timeZone);
                hours[0].name = this.options.dict.NextUp;
                return hours;
            }
        }
        return helpers.getDailyHours(this.provider.formatTime, 0, this.provider.timeZone);
    }
    displayDays(id) {
        return this.getDays(helpers.getMainheadingDaysName);
    }
    displayDaysSub(id) {
        return this.getDays(helpers.getSubheadingDaysName);
    }
    getDays(getHeadingName) {
        let dates = [];
        this.provider.events.forEach((event) => {
            const evDate = helpers.getISODate(event.startEventDate, this.provider.timeZone);
            if (evDate) {
                const dateId = this.getDayId(evDate);
                const shouldAdd = dates.find(x => x.id === dateId.toString()) === undefined;
                if (shouldAdd) {
                    dates.push(Object.assign({ id: dateId.toString(), sportId: this.sports[0].toString() }, getHeadingName(this.provider.formatHeadingDate, evDate, this.provider.timeZone, this.options.dict)));
                }
            }
        });
        return dates.sort((x, y) => x.id - y.id);
    }
    displayAllEvents(id) {
        const outrights = this.getOutrightsWithMbn(this.provider.events);
        return outrights.map(outright => {
            return {
                id: outright.e.id,
                sportId: outright.e.sportId,
                name: helpers.getEventTitle(outright.e, outright.m, this.options.dict.EachWayTag),
                mbnIcon: outright.e.mbnIcon,
                inLiveStreaming: this.filterManager.isCurrentMainFilter(FilterPredicate.LiveStreaming),
                marketsFilterEnabled: false
            };
        });
    }
    getOutrightsWithMbn(events) {
        return events.reduce((arr, event) => {
            const markets = this.displayMarkets(event.id);
            (markets.length > 0) && arr.push({
                e: Object.assign(Object.assign({}, event), { mbnIcon: markets[0].mbnIcon }),
                m: markets[0]
            });
            return arr;
        }, []);
    }
    displayEventByMainGrouping(id) {
        const events = this[MainGroupFunctions[this.mainGroupBy].events](id);
        if (this.isInFavoritesState) {
            return this.filterFavoriteEvents(events, true);
        }
        return events;
    }
    displayNoFilteredEvents(id) {
        return this.provider.events;
    }
    displaySelectedMarket() {
        const sportId = this.sports[0] || "0";
        return [{
                id: sportId,
                selectedMarketTypeId: this.getSelectedMarketTypeId,
                name: this.getMarketHeadingName(sportId, this.getSelectedMarketTypeId) || "",
                sportId,
                inLiveStreaming: this.filterManager.isCurrentMainFilter(FilterPredicate.LiveStreaming),
                marketsFilterEnabled: this.isMarketsDropdownFilterEnabled
                    && !this.isMiniEventCard
                    && !this.provider.isPersonalizedHighlights
            }];
    }
    displaySelectedMarketSub() {
        const sportId = this.sports[0] || "0";
        return [{
                id: this.getSelectedMarketTypeId,
                name: this.getMarketHeadingName(sportId, this.getSelectedMarketTypeId) || "",
                displayEvents: [],
                selectedMarketTypeId: this.getSelectedMarketTypeId,
                marketsFilterEnabled: this.options.cms.EnableMarketsDropdownFilter
                    && !this.isMiniEventCard
                    && !this.provider.isPersonalizedHighlights
            }];
    }
    getMarketHeadingName(sportId, selectedMarketTypeId) {
        var _a, _b;
        const marketTypeId = selectedMarketTypeId === null || selectedMarketTypeId === void 0 ? void 0 : selectedMarketTypeId.split(",")[0];
        return this.isMarketsDropdownFilterEnabled
            ? this.filterManager.isCurrentMainFilter(FilterPredicate.LiveStreaming)
                || this.provider.getCardConfigBySportId(sportId, this.isMiniEventCard).cardType === CardType.Multiview
                ? (_a = this.provider.events.find((ev) => ev.sportId === sportId)) === null || _a === void 0 ? void 0 : _a.sportName : marketTypeId && ((_b = this.getSelectedMarketType(marketTypeId)) === null || _b === void 0 ? void 0 : _b.name)
            : "";
    }
    getSelectedMarketType(selectedMarketTypeId) {
        var _a;
        return (_a = this.provider.markets.find(market => market.marketType.id === selectedMarketTypeId)) === null || _a === void 0 ? void 0 : _a.marketType;
    }
    displayEventsByMarket(marketTypeId) {
        return this.provider.events;
    }
    get shouldHideSubheadings() {
        return this.subGroupBy === SubGroupConfiguration.None;
    }
    get displayFavoriteEvents() {
        return this.provider.events.filter((ev) => ev.isInFavorites);
    }
    get displayFavoriteLeagues() {
        const favoriteLeagues = this.displayFavoriteEvents.reduce((acc, ev) => {
            acc[ev.leagueId] = {
                id: ev.leagueId,
                name: ev.leagueName,
                sportId: ev.sportId
            };
            return acc;
        }, {});
        return Object.keys(favoriteLeagues).map(key => favoriteLeagues[key]);
    }
    get showFavoritesSection() {
        return this.isFavoritesEnabled
            && !this.isInFavoritesState
            && this.displayFavoriteEvents.length > 0
            && this.subGroupBy === SubGroupConfiguration.League;
    }
    filterFavoriteEvents(events, showFavorites) {
        return events.filter((ev) => showFavorites ? ev.isInFavorites : !ev.isInFavorites);
    }
    get shouldShowOutrightSubheading() {
        return this.mainGroupBy !== MainGroupConfiguration.Event
            || this.subGroupBy !== SubGroupConfiguration.None;
    }
    get isGroupedByTime() {
        return this.subGroupBy === SubGroupConfiguration.Time;
    }
    getMarketTemplates(sportId) {
        const cardItem = this.provider.getCardConfigBySportId(sportId, this.isMiniEventCard);
        const templates = cardItem.markets.map(m => m.template);
        return templates;
    }
    generateSubheadingId(mainHeadingId, subheadingId) {
        const dateId = (this.dateRange && this.dateRange.from)
            ? `${this.dateRange.from.getDate()}-`
            : "";
        return `${mainHeadingId}-${dateId}${subheadingId}`;
    }
    shouldShowCustomSubheading(mainheading) {
        return this.deviceDetect.isDesktop
            && this.subGroupBy === SubGroupConfiguration.None
            && this.shouldShowNamesInHeading(mainheading)
            && this.displayEventByMainGrouping(mainheading.id).some((ev) => ev.type === EventTypes.Fixture.toString());
    }
    shouldShowNamesInHeading(mainheading, isMainHeading) {
        const cardType = this.provider.getCardConfigBySportId(mainheading.sportId, this.isMiniEventCard).cardType;
        const shouldShowForDevice = this.deviceDetect.isDesktop && !this.isMiniEventCard;
        const shouldShowForCard = isMainHeading
            ? cardType === CardType.Multiview
            : (cardType === CardType.American || cardType === CardType.Multiview);
        return shouldShowForDevice && shouldShowForCard && this.shouldShowNamesInHeadingForEvents;
    }
    get shouldShowNamesInHeadingForEvents() {
        if (this.mainGroupBy === MainGroupConfiguration.Event) {
            // Only outrights
            return false;
        }
        return this.provider.events.some(this.isFixture);
    }
    isFixture(ev) {
        return ev.type !== EventTypes.Outright;
    }
    getEventsHeight(events) {
        const eventChunkHeights = [];
        const eventToShow = Math.min(events.length, this.options.cms.ExpandedEvents);
        let baseMinHeight = 0;
        let currentEvent = {};
        let isFirstChunk = true;
        let offsetToAdd = 0;
        for (let current = 0; current < eventToShow;) {
            baseMinHeight = 0;
            const numberOfEvents = Math.min(eventToShow - current, this.EVENTS_CHUNK_SIZE);
            for (let i = 0; i < numberOfEvents; i++) {
                currentEvent = events[current + i];
                const markets = this.displayMarkets(currentEvent.id);
                offsetToAdd = isFirstChunk
                    ? this.componentManager.getOffsetValue(currentEvent)
                    : 0;
                baseMinHeight += this.componentManager.getComponentHeight(currentEvent, markets, this.showOddBtns) + offsetToAdd;
                isFirstChunk = false;
            }
            eventChunkHeights.push(baseMinHeight);
            const max = current + this.EVENTS_CHUNK_SIZE;
            current = Math.min(max, eventToShow);
        }
        return eventChunkHeights;
    }
    getDayId(evDate) {
        return new Date(evDate.getFullYear(), evDate.getMonth(), evDate.getDate(), 0, 0, 0).getTime();
    }
    isFavouriteLeague(sub) {
        return this.showFavoritesSection
            && this.displayFavoriteEvents.find(ev => ev.leagueId === sub.id);
    }
}
__decorate([
    observable,
    __metadata("design:type", Array)
], EventListPresenterState.prototype, "sports", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], EventListPresenterState.prototype, "dateRange", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], EventListPresenterState.prototype, "mainGroupBy", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], EventListPresenterState.prototype, "subGroupBy", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], EventListPresenterState.prototype, "isFavoritesEnabled", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], EventListPresenterState.prototype, "isInFavoritesState", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], EventListPresenterState.prototype, "loader", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], EventListPresenterState.prototype, "showOddBtns", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], EventListPresenterState.prototype, "isYourBetModeEnabled", void 0);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "isMiniEventCard", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "showViewMore", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "tooltipMessage", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], EventListPresenterState.prototype, "toggleOddsBtnOnClick", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], EventListPresenterState.prototype, "updateProviderProps", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "isLoading", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "hasLoadEvents", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "preliveOutrightShouldExpand", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "showLeagueInEventCard", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "getSelectedMarketTypeId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "getSportGroupingFilterConfig", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "getLiveStreamingGroupingFilterConfig", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "dateFilterConfig", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], EventListPresenterState.prototype, "changeSubGrouping", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], EventListPresenterState.prototype, "changeLiveStreamingFilter", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "displayMainHeadings", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String]),
    __metadata("design:returntype", void 0)
], EventListPresenterState.prototype, "onMarketTypeChange", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "shouldHideSubheadings", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "displayFavoriteEvents", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "displayFavoriteLeagues", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "showFavoritesSection", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "shouldShowOutrightSubheading", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "isGroupedByTime", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], EventListPresenterState.prototype, "shouldShowNamesInHeadingForEvents", null);
//# sourceMappingURL=EventListPresenterState.js.map